import { HotelClinicStatus } from "@/models/hotel-clinic.model";
import { CheckpointActionType } from "@/requests/hotel-departure.queries";
import {
    IHotelPlanningDepartureResponseItem,
    PatientHotelDepartureStatuses,
    getHotelDepartureRowId,
} from "@/schemas/hotelPlanningDepartureSchema";

/** Allowed driver status transitions (mirrors the hotel-clinic / driver mobile section). */
export const DRIVER_STATUS_TRANSITIONS: Partial<Record<HotelClinicStatus, HotelClinicStatus[]>> = {
    [HotelClinicStatus.PENDING]: [HotelClinicStatus.IN_TRANSIT],
    [HotelClinicStatus.IN_TRANSIT]: [HotelClinicStatus.PENDING, HotelClinicStatus.DONE],
    [HotelClinicStatus.DONE]: [HotelClinicStatus.IN_TRANSIT],
};

export type DepartureProcessAction = "start" | "complete";

/** custom passenger → target hotel-clinic task status per action. */
export const DEPARTURE_ACTION_TO_HOTEL_CLINIC_STATUS: Record<DepartureProcessAction, HotelClinicStatus> = {
    start: HotelClinicStatus.IN_TRANSIT,
    complete: HotelClinicStatus.DONE,
};

/** clinic passenger → checkpoint action per action. */
export const DEPARTURE_ACTION_TO_CHECKPOINT: Record<DepartureProcessAction, CheckpointActionType> = {
    start: CheckpointActionType.ENTER,
    complete: CheckpointActionType.EXIT,
};

/** Departure status that enables each action (both variants share this gating). */
export const DEPARTURE_ACTION_FROM_STATUS: Record<DepartureProcessAction, PatientHotelDepartureStatuses> = {
    start: PatientHotelDepartureStatuses.PENDING,
    complete: PatientHotelDepartureStatuses.IN_PROGRESS,
};

export type DepartureProcessItem =
    | {
          type: "clinic";
          rowId: string;
          action: DepartureProcessAction;
          operationId: string;
          uniqueCheckpointId: string;
      }
    | { type: "custom"; rowId: string; action: DepartureProcessAction; taskId: string };

/** Process is blocked until a driver is assigned (both variants). */
export const hasAssignedDriver = (row: IHotelPlanningDepartureResponseItem): boolean => !!row.assignedUser;

/**
 * Build a process item for a row + action, or null when it can't be processed:
 * no assigned driver, wrong current status, or a custom row without a task yet.
 */
export function buildDepartureProcessItem(
    row: IHotelPlanningDepartureResponseItem,
    action: DepartureProcessAction
): DepartureProcessItem | null {
    if (!hasAssignedDriver(row)) return null;
    if (row.status !== DEPARTURE_ACTION_FROM_STATUS[action]) return null;

    const rowId = getHotelDepartureRowId(row);
    if (row.type === "clinic") {
        return {
            type: "clinic",
            rowId,
            action,
            operationId: row.operationId,
            uniqueCheckpointId: row.uniqueCheckpointId,
        };
    }
    if (!row.taskId) return null;
    return { type: "custom", rowId, action, taskId: row.taskId };
}
